import React from 'react';
import { AwardIcon, CheckIcon } from 'lucide-react';
import { Tier } from '../types/commission';
interface TierStatusCardProps {
  tier: Tier;
  nextTier: Tier | null;
  totalTiers: number;
  tenureMonths: number;
  rollingUnits: number;
}
export function TierStatusCard({
  tier,
  nextTier,
  totalTiers,
  tenureMonths,
  rollingUnits
}: TierStatusCardProps) {
  const tenureGoal = 12;
  const unitsGoal = 26;
  return <section aria-labelledby="tier-status-heading" className="flex h-full flex-col overflow-hidden rounded-xl border border-brand-900 bg-brand-900 p-6 shadow-card" data-id="element-94" data-mp="25xucwuwjy3lq">
      <div className="flex items-start justify-between gap-4" data-id="element-95" data-mp="1x6bwcivp1ls4">
        <div data-id="element-96" data-mp="1x4h8owt1ffkc">
          <p className="text-xs font-semibold uppercase tracking-widest text-brand-200" data-id="element-97" data-mp="1x48wl74qoea7">Current tier</p>
          <h2 id="tier-status-heading" className="mt-1.5 text-2xl font-bold text-white" data-id="element-98" data-mp="2pmwb23p8cv2b">
            {tier.name}
          </h2>
        </div>
        <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/25" data-id="element-99" data-mp="2kv4tujrys1ou">
          <AwardIcon className="h-5 w-5 text-white" data-id="element-100" data-mp="3w3ywplxoxzlf" />
        </span>
      </div>

      <div className="mt-5" data-id="element-101" data-mp="4ujqoq7e5z2d">
        <div className="flex gap-1.5" aria-hidden="true" data-id="element-102" data-mp="3n9at007rkpyq">
          {Array.from({
          length: totalTiers
        }, (_, index) => <span key={index} className={`h-1.5 flex-1 rounded-full ${index < tier.level ? 'bg-gold-400' : 'bg-white/20'}`} data-id="element-103" data-mp="3ra4g4rddu0qk" />)}
        </div>
        <p className="mt-2 text-xs font-medium text-brand-200" data-id="element-104" data-mp="3d9to3l0c6g3t">
          Tier {tier.level} of {totalTiers} · {tier.baseBps} BPS base
        </p>
      </div>

      {nextTier ? <div className="mt-auto space-y-4 pt-6" data-id="element-105" data-mp="34bi1du2lc2gn">
          <p className="text-sm font-semibold text-white" data-id="element-106" data-mp="34ct3lpge125n">
            Next: {nextTier.name} <span className="font-medium text-brand-200" data-id="element-107" data-mp="38cbnqj0dx97f">({nextTier.baseBps} BPS base)</span>
          </p>
          <RequirementRow label="1 year at current tier" valueText={`${tenureMonths}/${tenureGoal} mo`} pct={Math.min(100, Math.round(tenureMonths / tenureGoal * 100))} met={tenureMonths >= tenureGoal} data-id="element-108" data-mp="12gz6zoo30qoc" />
          <RequirementRow label="26 units in 6 months" valueText={`${rollingUnits}/${unitsGoal}`} pct={Math.min(100, Math.round(rollingUnits / unitsGoal * 100))} met={rollingUnits >= unitsGoal} data-id="element-109" data-mp="2ffiy4htx9ojg" />
        </div> : <p className="mt-auto pt-6 text-sm font-semibold text-gold-300" data-id="element-110" data-mp="26is5ygihqtu5">Top tier reached.</p>}
    </section>;
}
interface RequirementRowProps {
  label: string;
  valueText: string;
  pct: number;
  met: boolean;
}
function RequirementRow({
  label,
  valueText,
  pct,
  met
}: RequirementRowProps) {
  return <div data-id="element-111" data-mp="2u8vxhpien34s">
      <div className="flex items-center justify-between gap-3 text-sm" data-id="element-112" data-mp="2pftbc2fyivym">
        <span className="flex items-center gap-2 text-brand-100" data-id="element-113" data-mp="2pdoy2tw64klo">
          {met ? <CheckIcon className="h-4 w-4 shrink-0 text-gold-400" aria-hidden="true" data-id="element-114" data-mp="2kfx8i6izxakn" /> : <span aria-hidden="true" className="h-4 w-4 shrink-0 rounded-full border border-brand-300" data-id="element-115" data-mp="3hwcdbsohh5eu" />}
          {label}
        </span>
        <span className="shrink-0 font-semibold tabular-nums text-white" data-id="element-116" data-mp="3d42sus650xjy">{valueText}</span>
      </div>
      <div role="progressbar" aria-label={label} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/15" data-id="element-117" data-mp="3kid2zgw70s0j">
        <div className={`h-full rounded-full ${met ? 'bg-gold-400' : 'bg-brand-400'}`} style={{
        width: `${pct}%`
      }} data-id="element-118" data-mp="3udwzo5ikzuqc" />
      </div>
    </div>;
}